import { InfiniteData, QueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../api/queryKeys';
import { ConversationMessageWithStatus } from '../../types/conversation.types';
import { ChatQuickReplyStatus } from '../../types/supportTicket.types';

/**
 * Reducers over the thread cache, shared by the send mutation and the socket.
 *
 * Keeping them here rather than inline in each caller is what makes the
 * optimistic row, the POST response and the socket echo agree: all three go
 * through the same `clientMessageId` matching, so they can arrive in any order.
 *
 * Page 0 is the NEWEST page (the API returns newest-first).
 */

type MessagePages = InfiniteData<
  ConversationMessageWithStatus[],
  number | undefined
>;

const messagesKey = (conversationId: number | string) =>
  queryKeys.conversations.messages(conversationId);

const mapPages = (
  data: MessagePages,
  fn: (
    page: ConversationMessageWithStatus[],
    index: number
  ) => ConversationMessageWithStatus[]
): MessagePages => ({ ...data, pages: data.pages.map(fn) });

/**
 * Insert a message, or reconcile it with a row that is already there.
 *
 * Matching is by `clientMessageId` first: an optimistic bubble carries the
 * same one the server echoes back, so whichever of the POST response and the
 * socket event lands first wins and the other is a no-op instead of a
 * duplicate. `id` is only a secondary guard for messages we never sent.
 *
 * That guard ignores id 0, which is the placeholder a row carries before it
 * has a server id - the optimistic send and Viktor's streamed answer both use
 * it. Matching on it pairs unrelated rows: two quick sends both had id 0, so
 * the second optimistic row replaced the first and a message vanished from
 * the thread; and Viktor's empty bubble replaced the question it was
 * answering, which then re-inserted itself as the newest row. A placeholder
 * is only ever identified by its `clientMessageId`.
 */
export const upsertChatMessage = (
  queryClient: QueryClient,
  conversationId: number | string,
  message: ConversationMessageWithStatus
) => {
  queryClient.setQueryData<MessagePages>(
    messagesKey(conversationId),
    (data: MessagePages | undefined) => {
      if (!data) return data;

      let replaced = false;
      const pages = data.pages.map((page: ConversationMessageWithStatus[]) => {
        const index = page.findIndex(
          (m: ConversationMessageWithStatus) =>
            (message.clientMessageId &&
              m.clientMessageId === message.clientMessageId) ||
            (message.id > 0 && m.id === message.id)
        );
        if (index === -1) return page;
        replaced = true;
        const next = page.slice();
        // Drop any client-only flags - this row is now server state.
        next[index] = message;
        return next;
      });

      if (replaced) return { ...data, pages };

      const [newest = [], ...rest] = data.pages;
      return { ...data, pages: [[message, ...newest], ...rest] };
    }
  );
};

/** Edit / soft delete - the row keeps its slot, so ordering never changes. */
export const replaceChatMessage = (
  queryClient: QueryClient,
  conversationId: number | string,
  message: ConversationMessageWithStatus
) => {
  queryClient.setQueryData<MessagePages>(
    messagesKey(conversationId),
    (data: MessagePages | undefined) =>
      data
        ? mapPages(data, (page) =>
            page.some((m) => m.id === message.id)
              ? page.map((m) => (m.id === message.id ? message : m))
              : page
          )
        : data
  );
};

/**
 * Take the Yes / No buttons off a question as soon as we know they are spent,
 * rather than when the socket gets round to saying so - a button still showing
 * after the tap invites a second one.
 *
 * `status` only when it is known: once our own answer landed it is `answered`,
 * but a 400 could mean any of three, and the refetch that follows says which.
 */
export const retireChatQuickReplies = (
  queryClient: QueryClient,
  conversationId: number | string,
  messageId: number,
  status?: ChatQuickReplyStatus
) => {
  queryClient.setQueryData<MessagePages>(
    messagesKey(conversationId),
    (data: MessagePages | undefined) =>
      data
        ? mapPages(data, (page) =>
            page.map((m) =>
              m.id === messageId && m.quickReplies
                ? {
                    ...m,
                    quickReplies: {
                      ...m.quickReplies,
                      active: false,
                      status: status ?? m.quickReplies.status,
                    },
                  }
                : m
            )
          )
        : data
  );
};

/** Flip an optimistic row to failed, or back to pending on retry. */
export const patchChatMessageByClientId = (
  queryClient: QueryClient,
  conversationId: number | string,
  clientMessageId: string,
  patch: Partial<ConversationMessageWithStatus>
) => {
  queryClient.setQueryData<MessagePages>(
    messagesKey(conversationId),
    (data: MessagePages | undefined) =>
      data
        ? mapPages(data, (page) =>
            page.map((m) =>
              m.clientMessageId === clientMessageId ? { ...m, ...patch } : m
            )
          )
        : data
  );
};

export const removeChatMessageByClientId = (
  queryClient: QueryClient,
  conversationId: number | string,
  clientMessageId: string
) => {
  queryClient.setQueryData<MessagePages>(
    messagesKey(conversationId),
    (data: MessagePages | undefined) =>
      data
        ? mapPages(data, (page) =>
            page.filter((m) => m.clientMessageId !== clientMessageId)
          )
        : data
  );
};

/**
 * Rewrite one row, found by `clientMessageId`, through `fn` - and say whether
 * it was there. The general form of `patchChatMessageByClientId` for a change
 * that depends on the row's current value: Viktor's streamed answer grows by
 * appending to whatever body it has, and its stored message swaps the row
 * wholesale. Returns false, and writes nothing, when no row matches - which
 * is how the caller learns the slot has to be created instead.
 */
export const patchChatMessage = (
  queryClient: QueryClient,
  conversationId: number | string,
  clientMessageId: string,
  fn: (row: ConversationMessageWithStatus) => ConversationMessageWithStatus
) => {
  if (!hasChatMessageByClientId(queryClient, conversationId, clientMessageId))
    return false;
  queryClient.setQueryData<MessagePages>(
    messagesKey(conversationId),
    (data: MessagePages | undefined) =>
      data
        ? mapPages(data, (page) =>
            page.map((m) => (m.clientMessageId === clientMessageId ? fn(m) : m))
          )
        : data
  );
  return true;
};

/** Whether a row with this `clientMessageId` is in the loaded history. */
export const hasChatMessageByClientId = (
  queryClient: QueryClient,
  conversationId: number | string,
  clientMessageId: string
) =>
  !!queryClient
    .getQueryData<MessagePages>(messagesKey(conversationId))
    ?.pages.some((page) =>
      page.some((m) => m.clientMessageId === clientMessageId)
    );

/**
 * The other side read up to `lastReadMessageId`, so every one of our messages
 * at or below that id is now seen. Ids are monotonic, which is what makes the
 * `<=` comparison valid.
 */
export const markChatMessagesSeen = (
  queryClient: QueryClient,
  conversationId: number | string,
  lastReadMessageId: number
) => {
  queryClient.setQueryData<MessagePages>(
    messagesKey(conversationId),
    (data: MessagePages | undefined) =>
      data
        ? mapPages(data, (page) =>
            page.map((m) =>
              !m.seen && m.id <= lastReadMessageId ? { ...m, seen: true } : m
            )
          )
        : data
  );
};

/**
 * Whether a read receipt reaching `lastReadMessageId` could still light a tick.
 *
 * Group threads answer `seen` from the server rather than from the receipt (see
 * `refreshSeenFlags`), and that answer costs a refetch of every loaded page.
 * Most receipts cannot change anything: they arrive alongside somebody else's
 * message, or from a colleague who is only catching up on ground the ticks
 * already cover. This is the cheap test for that - no unseen message of ours at
 * or below the marker, nothing a refetch could tell us.
 *
 * Pending rows are skipped: their `id` is a placeholder, so no receipt can be
 * about them. A null `senderId` means we cannot tell whose messages are whose
 * and every unseen row counts - the conservative side of the guess.
 */
export const hasUnseenOwnMessage = (
  queryClient: QueryClient,
  conversationId: number | string,
  senderId: number | null,
  lastReadMessageId: number
) => {
  const data = queryClient.getQueryData<MessagePages>(
    messagesKey(conversationId)
  );
  return !!data?.pages.some((page) =>
    page.some(
      (m) =>
        !m.seen &&
        !m.pending &&
        m.id <= lastReadMessageId &&
        (senderId === null || m.sender?.id === senderId)
    )
  );
};

/** True when the thread has been loaded at least once. */
export const hasChatMessagesCache = (
  queryClient: QueryClient,
  conversationId: number | string
) => queryClient.getQueryData(messagesKey(conversationId)) !== undefined;
